
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import appProviderService from '../../../../../services/api/apps/appProviderService';
import Modal from '../../../../../components/modals/Modal';
import { Button } from '../../../../../components/common';
import clsx from 'clsx';


function ModalAppProviderDetails({ open, provider = {}, onClose }: Props) {

    const providerQuery = useQuery({
        queryKey: ['payments', 'apps', 'providers', provider?.id],
        queryFn: () => appProviderService.getProvider(provider?.id),
        //initialData: { ...provider },
        placeholderData: { ...provider },
        // enabled: open,
        enabled: false,
        staleTime: Infinity,
        retryDelay: Infinity,
        refetchOnWindowFocus: false, // Disable refetching on window focus
        refetchOnMount: false, // Disable refetching on component mount
        refetchInterval: false, // Disable interval-based refetching
    });


    return (
        <Modal open={open} onClose={onClose}>
            <div className="inline-block w-[36rem] max-w-full">
                <div className="flex justify-between mb-5 px-10 pt-8">
                    <h2 className="font-medium text-xl">Provider Details</h2>

                    <button type="button" onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-500 focus:outline-none">
                        <i className="ri-close-line"></i>
                    </button>
                </div>


                <div className='max-h-[80vh] overflow-y-auto px-10 pb-6'>
                    <div className='divide-y dark:divide-gray-800 mb-8'>
                        <div className='flex items-end pb-4'>
                            <div className='flex-1'>
                                <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Name</p>
                                <p>{providerQuery.data?.name}</p>
                            </div>

                            <span className={clsx('text-xs font-medium rounded-lg py-1.5 px-2.5', {
                                'bg-green-600 text-white': providerQuery.data?.status === 'ACTIVE',
                                'bg-yellow-600 text-white': providerQuery.data?.status === 'INACTIVE',
                            })}>
                                {providerQuery.data?.status}
                            </span>
                        </div>

                        <div className='py-4'>
                            <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Description</p>
                            <p>{providerQuery.data?.description}</p>
                        </div>

                        <div className='py-4'>
                            <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Web Address</p>
                            <p>{providerQuery.data?.webAddress}</p>
                        </div>

                        <div className='py-4'>
                            <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>GUID</p>
                            <p>{providerQuery.data?.guid}</p>
                        </div>

                        {
                            providerQuery.data?.appCategories &&
                            <div className='py-4'>
                                <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Categories</p>

                                {
                                    providerQuery.data?.appCategories?.length > 0 &&
                                    <div className='flex items-center flex-wrap gap-4'>
                                        {
                                            providerQuery.data?.appCategories?.map((item: any, itemIndex: number) =>
                                                <span key={item.id} title={item.description} className='bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 font-medium rounded-lg py-2.5 px-3.5'>
                                                    {item.name}
                                                </span>
                                            )
                                        }
                                    </div>
                                }
                            </div>
                        }

                        <div className='py-4'>
                            <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Provider Logo</p>
                            <div className='bg-white max-w-28'>
                                <img src={providerQuery.data?.logo} alt={`${providerQuery.data?.name} logo`} />
                            </div>
                        </div>

                        <div className='grid grid-cols-2'>
                            <div className='pt-4'>
                                <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Date Created</p>
                                {providerQuery.data?.createdDate && <p>{format(new Date(providerQuery.data.createdDate), 'd LLL yyyy h:mm a')}</p>}
                            </div>
                        </div>
                    </div>


                    <div className='flex justify-end items-center'>
                        <Button type='button' onClick={onClose} className='!text-gray-500 dark:!text-white bg-gray-100 hocus:bg-gray-200  dark:bg-gray-800 dark:hocus:bg-gray-600 py-2.5'>
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}


interface Props {
    open: boolean;
    provider: any;
    onClose: () => void;
}


export default ModalAppProviderDetails;